import React, { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import ServiceMaterialModal from '../../service-and-checklist/components/ServiceMaterialModal';
import { serviceMaterialService } from '../../../services/supabaseService';

const ServiceEditPanel = ({ 
  mode = 'add',
  selectedNode = null,
  parentNode = null,
  categories = [],
  equipments = [],
  problems = [],
  onSave, 
  onCancel 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'category',
    category_id: '',
    equipment_id: '',
    problem_id: '',
    pricingModel: 'fixed',
    basePrice: '',
    vatRate: '20',
    warrantyMonths: '6',
    estimatedDuration: '',
    skillRequirements: [],
    seasonalPricing: false,
    geographicAvailability: 'all',
    customerValidationRequired: false,
    allowFieldAmendments: true,
    active: true,
    image: null,
    logo: null,
    imageUrl: '',
    logoUrl: '',
    problemPhotos: [],
    photoDescription: '',
    produit_fournissable: false,
    prix_sans_produit: '',
    description_produit: '',
    image_produit: '',
    produitImage: null,
    necessite_materiel: true,
  });

  const [errors, setErrors] = useState({});
  const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);
  const [serviceMaterials, setServiceMaterials] = useState([]);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [availableEquipments, setAvailableEquipments] = useState([]);
  const [availableProblems, setAvailableProblems] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [photoInputKey, setPhotoInputKey] = useState(Date.now());
  const imageInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const produitImageInputRef = useRef(null);
  const [produitImagePreview, setProduitImagePreview] = useState(null);

  // Helper function to convert photos from array of URLs to array of objects with preview
  const convertPhotosToDisplayFormat = (photos) => {
    if (!photos || !Array.isArray(photos)) return [];
    
    return photos.map((photo, index) => {
      // Si c'est une URL (string)
      if (typeof photo === 'string') {
        return {
          id: `existing_${Date.now()}_${index}`,
          url: photo,
          preview: photo,
          description: '',
          isMain: index === 0,
          isExisting: true,
          file: null
        };
      }
      // Si c'est déjà un objet
      if (typeof photo === 'object' && photo.url) {
        return {
          id: `existing_${Date.now()}_${index}`,
          url: photo.url,
          preview: photo.url,
          description: photo.description || '',
          isMain: photo.isMain || index === 0,
          isExisting: true,
          file: null
        };
      }
      return null;
    }).filter(p => p !== null);
  };

  useEffect(() => {
    let newFormData = {
      name: '',
      description: '',
      type: 'category',
      category_id: '',
      equipment_id: '',
      problem_id: '',
      pricingModel: 'fixed',
      basePrice: '',
      vatRate: '20',
      warrantyMonths: '6',
      estimatedDuration: '',
      skillRequirements: [],
      seasonalPricing: false,
      geographicAvailability: 'all',
      customerValidationRequired: false,
      allowFieldAmendments: true,
      active: true,
      image: null,
      logo: null,
      imageUrl: '',
      logoUrl: '',
      problemPhotos: [],
      photoDescription: '',
      produit_fournissable: false,
      prix_sans_produit: '',
      description_produit: '',
      image_produit: '',
      produitImage: null,
      necessite_materiel: true,
    };

    if (mode === 'add') {
      setImagePreview(null);
      setLogoPreview(null);
      setProduitImagePreview(null);
    }

    if (mode === 'create-child' && parentNode) {
      if (parentNode.type === 'category') {
        newFormData = {
          ...newFormData,
          type: 'equipment',
          category_id: parentNode.id,
        };
      } else if (parentNode.type === 'equipment') {
        newFormData = {
          ...newFormData,
          type: 'problem',
          equipment_id: parentNode.id,
          category_id: parentNode.category_id || parentNode.categoryId || '',
        };
      } else if (parentNode.type === 'problem') {
        newFormData = {
          ...newFormData,
          type: 'service',
          problem_id: parentNode.id,
          equipment_id: parentNode.equipment_id || parentNode.equipmentId || '',
          category_id: parentNode.category_id || parentNode.categoryId || ''
        };
      }
    } else if (mode === 'edit' && selectedNode) {
      // Récupérer les photos depuis selectedNode.photos (pas problemPhotos)
      let existingPhotos = [];
      if (selectedNode.type === 'problem' && selectedNode.photos) {
        existingPhotos = convertPhotosToDisplayFormat(selectedNode.photos);
        console.log('📸 Photos existantes chargées:', existingPhotos);
      }
      
      newFormData = {
        name: selectedNode.name || '',
        description: selectedNode.description || '',
        type: selectedNode.type || 'category',
        category_id: selectedNode.category_id || selectedNode.categoryId || '',
        equipment_id: selectedNode.equipment_id || selectedNode.equipmentId || '',
        problem_id: selectedNode.problem_id || selectedNode.problemId || '',
        pricingModel: selectedNode.pricingModel || 'fixed',
        basePrice: selectedNode.basePrice || selectedNode.prix_avec_produit || '',
        vatRate: selectedNode.vatRate || '20',
        warrantyMonths: selectedNode.warrantyMonths || '6',
        estimatedDuration: selectedNode.estimatedDuration || '',
        skillRequirements: selectedNode.skillRequirements || [],
        seasonalPricing: selectedNode.seasonalPricing || false,
        geographicAvailability: selectedNode.geographicAvailability || 'all',
        customerValidationRequired: selectedNode.customerValidationRequired || false,
        allowFieldAmendments: selectedNode.allowFieldAmendments !== false,
        active: selectedNode.active !== false,
        image: null,
        logo: null,
        imageUrl: selectedNode.imageUrl || '',
        logoUrl: selectedNode.logoUrl || '',
        problemPhotos: existingPhotos,
        photoDescription: '',
        produit_fournissable: Boolean(selectedNode.produit_fournissable),
        prix_sans_produit: selectedNode.prix_sans_produit ?? '',
        description_produit: selectedNode.description_produit || '',
        image_produit: selectedNode.image_produit || '',
        produitImage: null,
        necessite_materiel: selectedNode.necessite_materiel !== false,
      };

      if (selectedNode.image_produit) {
        setProduitImagePreview(selectedNode.image_produit);
      } else {
        setProduitImagePreview(null);
      }
      if (selectedNode.imageUrl) {
        setImagePreview(selectedNode.imageUrl);
      }
      if (selectedNode.logoUrl) {
        setLogoPreview(selectedNode.logoUrl);
      }
    }

    setFormData(newFormData);
  }, [mode, selectedNode, parentNode]);

  const editedServiceId = mode === 'edit' && selectedNode?.type === 'service' ? selectedNode.id : null;

  useEffect(() => {
    setServiceMaterials([]);
    if (!editedServiceId) return undefined;
    let cancelled = false;
    setMaterialsLoading(true);
    serviceMaterialService.getByService(editedServiceId).then(({ data, error }) => {
      if (cancelled) return;
      if (error) console.error('Erreur chargement matériel:', error);
      setServiceMaterials(data || []);
      setMaterialsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [editedServiceId]);

  const handleImageUpload = (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.match('image.*')) {
      alert('Veuillez sélectionner un fichier image valide (JPEG, PNG, etc.)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('L\'image est trop grande. Taille maximum: 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (type === 'image') {
        setImagePreview(e.target.result);
        setFormData(prev => ({ 
          ...prev, 
          image: file,
          imageUrl: ''
        }));
      } else if (type === 'logo') {
        setLogoPreview(e.target.result);
        setFormData(prev => ({ 
          ...prev, 
          logo: file,
          logoUrl: ''
        }));
      } else if (type === 'produit') {
        setProduitImagePreview(e.target.result);
        setFormData(prev => ({
          ...prev,
          produitImage: file,
          image_produit: '',
        }));
      }
    };
    reader.readAsDataURL(file);
  };

  const handleProblemPhotoUpload = (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    const newPhotos = files.map((file, index) => {
      if (!file.type.match('image.*')) {
        alert(`Le fichier ${file.name} n'est pas une image valide`);
        return null;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert(`L'image ${file.name} est trop grande. Taille maximum: 5MB`);
        return null;
      }

      const preview = URL.createObjectURL(file);
      return {
        id: Date.now() + index,
        file: file,
        preview: preview,
        description: '',
        isMain: formData.problemPhotos.length === 0 && index === 0,
        isExisting: false
      };
    }).filter(photo => photo !== null);

    setFormData(prev => ({
      ...prev,
      problemPhotos: [...prev.problemPhotos, ...newPhotos]
    }));

    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
    setPhotoInputKey(Date.now());
  };

  const updatePhotoDescription = (photoId, description) => {
    setFormData(prev => ({
      ...prev,
      problemPhotos: prev.problemPhotos.map(photo =>
        photo.id === photoId ? { ...photo, description } : photo
      )
    }));
  };

  const removeProblemPhoto = (photoId) => {
    setFormData(prev => {
      const filteredPhotos = prev.problemPhotos.filter(photo => photo.id !== photoId);
      if (filteredPhotos.length > 0 && !filteredPhotos.some(p => p.isMain)) {
        filteredPhotos[0].isMain = true;
      }
      return { ...prev, problemPhotos: filteredPhotos };
    });
  };

  const setMainPhoto = (photoId) => {
    setFormData(prev => ({
      ...prev,
      problemPhotos: prev.problemPhotos.map(photo => ({
        ...photo,
        isMain: photo.id === photoId
      }))
    }));
  };

  const handleRemoveImage = (type) => {
    if (type === 'image') {
      setImagePreview(null);
      setFormData(prev => ({ 
        ...prev, 
        image: null,
        imageUrl: ''
      }));
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    } else if (type === 'logo') {
      setLogoPreview(null);
      setFormData(prev => ({ 
        ...prev, 
        logo: null,
        logoUrl: ''
      }));
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    } else if (type === 'produit') {
      setProduitImagePreview(null);
      setFormData(prev => ({
        ...prev,
        produitImage: null,
        image_produit: '',
      }));
      if (produitImageInputRef.current) {
        produitImageInputRef.current.value = '';
      }
    }
  };

  useEffect(() => {
    if (formData.imageUrl && !imagePreview && !formData.image) {
      setImagePreview(formData.imageUrl);
    }
    if (formData.logoUrl && !logoPreview && !formData.logo) {
      setLogoPreview(formData.logoUrl);
    }
  }, [formData.imageUrl, formData.logoUrl, formData.image, formData.logo]);

  useEffect(() => {
    if (formData.category_id && (formData.type === 'problem' || formData.type === 'service')) {
      const filtered = equipments.filter(eq => 
        eq.category_id === formData.category_id || 
        eq.category === formData.category_id
      );
      setAvailableEquipments(filtered);
    } else {
      setAvailableEquipments([]);
    }
  }, [formData.category_id, formData.type, equipments]);

  useEffect(() => {
    if (formData.equipment_id && formData.type === 'service') {
      const filtered = problems.filter(p => 
        p.equipment_id === formData.equipment_id || 
        p.equipment === formData.equipment_id
      );
      setAvailableProblems(filtered);
    } else {
      setAvailableProblems([]);
    }
  }, [formData.equipment_id, formData.type, problems]);

  const typeOptions = [
    { value: 'category', label: 'Catégorie' },
    { value: 'equipment', label: 'Équipement' },
    { value: 'problem', label: 'Problème' },
    { value: 'service', label: 'Service' }
  ];

  const pricingModelOptions = [
    { value: 'fixed', label: 'Prix fixe', description: 'Tarif défini à l\'avance' },
    { value: 'quote', label: 'Sur devis', description: 'Nécessite une évaluation' }
  ];

  const vatRateOptions = [
    { value: '10', label: '10% (Particuliers)' },
    { value: '20', label: '20% (Entreprises)' }
  ];

  const skillOptions = [
    { value: 'plumbing_basic', label: 'Plomberie de base' },
    { value: 'plumbing_advanced', label: 'Plomberie avancée' },
    { value: 'heating', label: 'Chauffage' },
    { value: 'drainage', label: 'Assainissement' },
    { value: 'gas_certified', label: 'Gaz (certifié)' },
    { value: 'emergency', label: 'Interventions d\'urgence' }
  ];

  const categoryOptions = categories.map(cat => ({
    value: cat.id,
    label: cat.name
  }));

  const equipmentOptions = availableEquipments.map(eq => ({
    value: eq.id,
    label: eq.name
  }));

  const problemOptions = availableProblems.map(p => ({
    value: p.id,
    label: p.name
  }));

  const handleChange = useCallback((field, value) => {
    setFormData(prev => {
      const updates = { [field]: value };
      
      if (field === 'type') {
        updates.category_id = '';
        updates.equipment_id = '';
        updates.problem_id = '';
        if (value !== 'category' && value !== 'equipment') {
          updates.image = null;
          updates.logo = null;
          updates.imageUrl = '';
          updates.logoUrl = '';
          setImagePreview(null);
          setLogoPreview(null);
        }
        if (value !== 'problem') {
          updates.problemPhotos = [];
        }
      } else if (field === 'category_id') {
        updates.equipment_id = '';
        updates.problem_id = '';
      } else if (field === 'equipment_id') {
        updates.problem_id = '';
      }
      
      return { ...prev, ...updates };
    });
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Le nom est obligatoire';
    }

    if (formData.type === 'equipment' && !formData.category_id) {
      newErrors.category_id = 'Veuillez sélectionner une catégorie';
    }

    if (formData.type === 'problem') {
      if (!formData.category_id) {
        newErrors.category_id = 'Veuillez sélectionner une catégorie';
      }
      if (!formData.equipment_id) {
        newErrors.equipment_id = 'Veuillez sélectionner un équipement';
      }
    }

    if (formData.type === 'service') {
      if (!formData.category_id) {
        newErrors.category_id = 'Veuillez sélectionner une catégorie';
      }
      if (!formData.equipment_id) {
        newErrors.equipment_id = 'Veuillez sélectionner un équipement';
      }
      if (!formData.problem_id) {
        newErrors.problem_id = 'Veuillez sélectionner un problème';
      }

      if (formData.pricingModel === 'fixed') {
        if (!formData.basePrice) {
          newErrors.basePrice = formData.necessite_materiel
            ? 'Le prix sans matériel est obligatoire'
            : 'Le prix est obligatoire';
        }
        // Produit fourni par le client : désactivé pour le moment
        // if (formData.produit_fournissable) {
        //   if (!formData.prix_sans_produit) {
        //     newErrors.prix_sans_produit = 'Le prix sans produit est obligatoire';
        //   }
        //   if (!formData.description_produit?.trim()) {
        //     newErrors.description_produit = 'La description du produit est obligatoire';
        //   }
        // }
      }
      if (!formData.estimatedDuration) {
        newErrors.estimatedDuration = 'La durée estimée est obligatoire';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      // Préparer les photos pour la sauvegarde - extraire seulement les URLs pour les photos existantes
      const photosToSave = formData.problemPhotos.map(photo => {
        if (photo.isExisting && photo.url) {
          return photo.url;
        } else if (photo.file) {
          return { file: photo.file, description: photo.description, isMain: photo.isMain };
        }
        return null;
      }).filter(p => p !== null);
      
      const dataToSave = {
        ...formData,
        id: mode === 'edit' ? selectedNode?.id : (formData.id || undefined),
        category_id: formData.category_id || undefined,
        equipment_id: formData.equipment_id || undefined,
        problem_id: formData.problem_id || undefined,
        imageUrl: formData.imageUrl || undefined,
        logoUrl: formData.logoUrl || undefined,
        image_produit: formData.image_produit || undefined,
        photos: photosToSave // Utiliser "photos" pas "problemPhotos"
      };
      
      console.log('💾 Données à sauvegarder:', dataToSave);
      onSave(dataToSave);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'add':
        return 'Ajouter un nouvel élément';
      case 'edit':
        return `Modifier ${selectedNode?.name || 'l\'élément'}`;
      case 'create-child':
        return `Ajouter un ${formData.type} à ${parentNode?.name || 'l\'élément'}`;
      default:
        return 'Éditeur';
    }
  };

  const getIcon = () => {
    switch (mode) {
      case 'add':
        return 'Plus';
      case 'edit':
        return 'Edit';
      case 'create-child':
        return 'PlusCircle';
      default:
        return 'Edit';
    }
  };

  if (!mode && !selectedNode) {
    return (
      <div className="h-full flex items-center justify-center text-center p-8">
        <div>
          <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Sélectionnez ou créez un élément</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="h-full flex flex-col">
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name={getIcon()} size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {getTitle()}
            </h2>
            <p className="text-sm text-muted-foreground">
              Type: {formData.type}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            iconName="X"
            onClick={onCancel}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            iconName="Save"
          >
            {mode === 'edit' ? 'Modifier' : 'Créer'}
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="bg-card rounded-lg border border-border p-6 space-y-4">
          <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
            <Icon name="Info" size={18} />
            Informations générales
          </h3>

          {mode === 'add' && (
            <Select
              label="Type d'élément"
              options={typeOptions}
              value={formData.type}
              onChange={(value) => handleChange('type', value)}
              required
            />
          )}

          {(formData.type === 'equipment' || formData.type === 'problem' || formData.type === 'service') && 
           !(mode === 'create-child' && parentNode?.type === 'category') && (
            <Select
              label="Catégorie associée"
              options={categoryOptions}
              value={formData.category_id}
              onChange={(value) => handleChange('category_id', value)}
              error={errors.category_id}
              required={formData.type !== 'category'}
              disabled={mode === 'create-child' || mode === 'edit'}
              placeholder="Sélectionnez une catégorie"
            />
          )}

          {(formData.type === 'problem' || formData.type === 'service') && 
           formData.category_id && 
           !(mode === 'create-child' && parentNode?.type === 'equipment') && (
            <Select
              label="Équipement associé"
              options={equipmentOptions}
              value={formData.equipment_id}
              onChange={(value) => handleChange('equipment_id', value)}
              error={errors.equipment_id}
              required={formData.type === 'problem' || formData.type === 'service'}
              disabled={mode === 'create-child' || mode === 'edit'}
              placeholder={
                availableEquipments.length === 0 
                  ? "Aucun équipement disponible pour cette catégorie" 
                  : "Sélectionnez un équipement"
              }
            />
          )}

          {formData.type === 'service' && 
           formData.equipment_id && 
           !(mode === 'create-child' && parentNode?.type === 'problem') && (
            <Select
              label="Problème associé"
              options={problemOptions}
              value={formData.problem_id}
              onChange={(value) => handleChange('problem_id', value)}
              error={errors.problem_id}
              required
              disabled={mode === 'create-child' || mode === 'edit'}
              placeholder={
                availableProblems.length === 0 
                  ? "Aucun problème disponible pour cet équipement" 
                  : "Sélectionnez un problème"
              }
            />
          )}

          <Input
            label="Nom"
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            error={errors.name}
            required
            placeholder={
              formData.type === 'category' ? 'Ex: Plomberie' :
              formData.type === 'equipment' ? 'Ex: Toilette' :
              formData.type === 'problem' ? 'Ex: Fuite d\'eau' :
              'Ex: Réparation de fuite'
            }
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder={`Description détaillée du ${formData.type}...`}
            />
          </div>

          {/* SECTION PHOTOS POUR LES PROBLÈMES */}
          {formData.type === 'problem' && (
            <div className="space-y-4 border-t border-border pt-4 mt-2">
              <div className="flex items-center gap-2">
                <Icon name="Camera" size={18} className="text-primary" />
                <h4 className="text-sm font-semibold text-foreground">Photos d'illustration du problème</h4>
              </div>
              
              <p className="text-xs text-muted-foreground">
                Ajoutez des photos pour illustrer ce problème. Ces photos aideront les clients à identifier le problème.
              </p>

              {/* Upload de photos */}
              <div className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
                <input
                  key={photoInputKey}
                  type="file"
                  ref={photoInputRef}
                  accept="image/*"
                  multiple
                  onChange={handleProblemPhotoUpload}
                  className="hidden"
                  id="problem-photo-upload"
                />
                <label htmlFor="problem-photo-upload" className="cursor-pointer">
                  <Icon name="Upload" size={32} className="mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm font-medium text-foreground mb-1">
                    Cliquez pour ajouter des photos
                  </p>
                  <p className="text-xs text-muted-foreground">
                    JPEG, PNG, GIF. Max 5MB par photo
                  </p>
                </label>
              </div>

              {/* Liste des photos avec descriptions */}
              {formData.problemPhotos.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-foreground">
                      {formData.problemPhotos.length} photo(s)
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formData.problemPhotos.filter(p => p.isMain).length ? "✓ Photo principale définie" : "Sélectionnez une photo principale"}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4 max-h-96 overflow-y-auto">
                    {formData.problemPhotos.map((photo, index) => (
                      <div key={photo.id} className="border border-border rounded-lg p-3 bg-muted/20">
                        <div className="flex gap-3">
                          {/* Aperçu de la photo */}
                          <div className="w-24 h-24 flex-shrink-0 border border-border rounded-lg overflow-hidden bg-white">
                            <img 
                              src={photo.preview || photo.url} 
                              alt={`Photo ${index + 1}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = '/placeholder-image.jpg';
                              }}
                            />
                          </div>
                          
                          {/* Zone de description */}
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              {!photo.isMain && (
                                <button
                                  type="button"
                                  onClick={() => setMainPhoto(photo.id)}
                                  className="text-xs text-primary hover:underline"
                                >
                                  Définir comme principale
                                </button>
                              )}
                              {photo.isMain && (
                                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                                  Photo principale
                                </span>
                              )}
                              {photo.isExisting && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                  Existante
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => removeProblemPhoto(photo.id)}
                                className="text-xs text-destructive hover:underline ml-auto"
                              >
                                Supprimer
                              </button>
                            </div>
                            
                            <div>
                              <label className="text-xs font-medium text-foreground">
                                Description de la photo
                              </label>
                              <textarea
                                value={photo.description}
                                onChange={(e) => updatePhotoDescription(photo.id, e.target.value)}
                                rows={2}
                                className="w-full mt-1 px-2 py-1 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                                placeholder="Ex: Vue d'ensemble de la fuite sous l'évier..."
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                {photo.description.length}/200 caractères
                              </p>
                            </div>
                            
                            {/* Afficher l'URL si c'est une photo existante */}
                            {photo.isExisting && photo.url && (
                              <p className="text-xs text-muted-foreground truncate">
                                URL: {photo.url}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Champs d'upload d'images pour les catégories et équipements */}
          {(formData.type === 'category' || formData.type === 'equipment') && (
            <>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  {formData.type === 'category' ? 'Image de la catégorie' : 'Image de l\'équipement'}
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    ref={imageInputRef}
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'image')}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => imageInputRef.current?.click()}
                    iconName="Upload"
                  >
                    {formData.imageUrl ? 'Changer l\'image' : 'Choisir une image'}
                  </Button>
                  {(imagePreview || formData.imageUrl) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveImage('image')}
                      iconName="X"
                      className="text-destructive hover:text-destructive"
                    >
                      Supprimer
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Formats acceptés: JPEG, PNG, GIF. Taille max: 5MB
                </p>
                
                {(imagePreview || formData.imageUrl) && (
                  <div className="mt-3">
                    <p className="text-xs text-muted-foreground mb-1">
                      {formData.image ? 'Nouvelle image' : 'Image existante'}
                    </p>
                    <div className="w-32 h-32 border border-border rounded-lg overflow-hidden">
                      <img 
                        src={imagePreview || formData.imageUrl} 
                        alt="Image preview" 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          console.error('❌ Erreur chargement image:', imagePreview || formData.imageUrl);
                          e.target.src = '/placeholder-image.jpg';
                          e.target.alt = 'Image non chargée';
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  {formData.type === 'category' ? 'Logo de la catégorie' : 'Icône de l\'équipement'}
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    ref={logoInputRef}
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'logo')}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => logoInputRef.current?.click()}
                    iconName="Upload"
                  >
                    {formData.logoUrl ? 'Changer le logo' : 'Choisir un logo'}
                  </Button>
                  {(logoPreview || formData.logoUrl) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveImage('logo')}
                      iconName="X"
                      className="text-destructive hover:text-destructive"
                    >
                      Supprimer
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Formats acceptés: JPEG, PNG, GIF, SVG. Taille max: 5MB
                </p>
                
                {(logoPreview || formData.logoUrl) && (
                  <div className="mt-3">
                    <p className="text-xs text-muted-foreground mb-1">
                      {formData.logo ? 'Nouveau logo' : 'Logo existant'}
                    </p>
                    <div className="w-32 h-32 border border-border rounded-lg flex items-center justify-center bg-muted/30">
                      <img 
                        src={logoPreview || formData.logoUrl} 
                        alt="Logo preview" 
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          console.error('❌ Erreur chargement logo:', logoPreview || formData.logoUrl);
                          e.target.src = '/placeholder-logo.png';
                          e.target.alt = 'Logo non chargé';
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <Checkbox
            label="Actif"
            description="L'élément est visible et disponible"
            checked={formData.active}
            onChange={(e) => handleChange('active', e.target.checked)}
          />
        </div>

        {formData.type === 'service' && (
          <>
            <div className="bg-card rounded-lg border border-border p-6 space-y-4">
              <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                <Icon name="DollarSign" size={18} />
                Tarification
              </h3>

              <Select
                label="Modèle de tarification"
                options={pricingModelOptions}
                value={formData.pricingModel}
                onChange={(value) => handleChange('pricingModel', value)}
                required
              />

              {formData.pricingModel === 'fixed' && (
                <>
                  <Checkbox
                    label="Ce service nécessite un matériel"
                    description="Le client choisit un matériel parmi ceux proposés ; son prix s'ajoute au prix du service"
                    checked={formData.necessite_materiel}
                    onChange={(e) => handleChange('necessite_materiel', e.target.checked)}
                  />

                  {formData.necessite_materiel && (
                    <div className="p-4 rounded-lg border border-border bg-muted/30">
                      {editedServiceId ? (
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground">
                              {materialsLoading
                                ? 'Chargement du matériel...'
                                : `${serviceMaterials.length} matériel${serviceMaterials.length > 1 ? 's' : ''}`}
                            </p>
                            {!materialsLoading && (
                              <p className="text-sm text-muted-foreground truncate">
                                {serviceMaterials.length === 0
                                  ? 'Aucun matériel pour ce service'
                                  : `Par défaut : ${
                                      serviceMaterials.find((m) => m.is_default)?.nom || 'aucun'
                                    }`}
                              </p>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            iconName="Package"
                            iconPosition="left"
                            iconSize={14}
                            onClick={() => setIsMaterialModalOpen(true)}
                          >
                            Gérer le matériel
                          </Button>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground flex items-start gap-2">
                          <Icon name="Info" size={16} className="mt-0.5 flex-shrink-0" />
                          Enregistrez d'abord le service pour pouvoir ajouter son matériel.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Produit fourni par le client : désactivé pour le moment
                  <Checkbox
                    label="Produit fourni par l'entreprise"
                    description="Le client peut choisir de fournir le produit lui-même (prix différent)"
                    checked={formData.produit_fournissable}
                    onChange={(e) => handleChange('produit_fournissable', e.target.checked)}
                  />
                  */}

                  <Input
                    label={
                      formData.necessite_materiel
                        ? 'Prix sans matériel (€ HT)'
                        : 'Prix (€ HT)'
                    }
                    type="number"
                    value={formData.basePrice}
                    onChange={(e) => handleChange('basePrice', e.target.value)}
                    error={errors.basePrice}
                    required
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />

                  {/* Produit fourni par le client : désactivé pour le moment
                  {formData.produit_fournissable && (
                    <>
                      <Input
                        label="Prix sans produit — client fournit (€ HT)"
                        type="number"
                        value={formData.prix_sans_produit}
                        onChange={(e) => handleChange('prix_sans_produit', e.target.value)}
                        error={errors.prix_sans_produit}
                        required
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Description du produit fourni
                        </label>
                        <textarea
                          value={formData.description_produit}
                          onChange={(e) => handleChange('description_produit', e.target.value)}
                          className={`w-full min-h-[100px] px-3 py-2 rounded-lg border bg-background text-foreground ${
                            errors.description_produit ? 'border-error' : 'border-border'
                          }`}
                          placeholder="Ex. Robinet mitigeur chromé inclus, pose comprise..."
                        />
                        {errors.description_produit && (
                          <p className="text-sm text-error mt-1">{errors.description_produit}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Image du produit (optionnel)
                        </label>
                        {produitImagePreview ? (
                          <div className="relative inline-block">
                            <img
                              src={produitImagePreview}
                              alt="Produit"
                              className="w-32 h-32 object-cover rounded-lg border border-border"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="absolute -top-2 -right-2"
                              onClick={() => handleRemoveImage('produit')}
                            >
                              <Icon name="X" size={14} />
                            </Button>
                          </div>
                        ) : (
                          <input
                            ref={produitImageInputRef}
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, 'produit')}
                            className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                          />
                        )}
                      </div>
                    </>
                  )}
                  */}

                  <Select
                    label="Taux de TVA"
                    description="10% pour particuliers, 20% pour entreprises"
                    options={vatRateOptions}
                    value={formData.vatRate}
                    onChange={(value) => handleChange('vatRate', value)}
                    required
                  />

                  {formData.basePrice && (
                    <div className="p-4 bg-muted rounded-lg">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Prix HT:</span>
                        <span className="font-medium">
                          {parseFloat(formData.basePrice).toFixed(2)} €
                        </span>
                      </div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-muted-foreground">TVA ({formData.vatRate}%):</span>
                        <span className="font-medium">
                          {(
                            parseFloat(formData.basePrice) *
                            parseFloat(formData.vatRate) /
                            100
                          ).toFixed(2)}{' '}
                          €
                        </span>
                      </div>
                      <div className="flex justify-between text-base font-semibold pt-2 border-t border-border">
                        <span>Prix TTC:</span>
                        <span className="text-primary">
                          {(
                            parseFloat(formData.basePrice) *
                            (1 + parseFloat(formData.vatRate) / 100)
                          ).toFixed(2)}{' '}
                          €
                        </span>
                      </div>
                      {formData.necessite_materiel && (
                        <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
                          Hors matériel : le prix du matériel choisi par le client s'ajoute à ce montant.
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}

              <Checkbox
                label="Tarification saisonnière"
                description="Appliquer des prix différents selon la saison"
                checked={formData.seasonalPricing}
                onChange={(e) => handleChange('seasonalPricing', e.target.checked)}
              />
            </div>

            <div className="bg-card rounded-lg border border-border p-6 space-y-4">
              <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                <Icon name="Settings" size={18} />
                Configuration du service
              </h3>

              <Input
                label="Durée estimée (minutes)"
                type="number"
                value={formData.estimatedDuration}
                onChange={(e) => handleChange('estimatedDuration', e.target.value)}
                error={errors.estimatedDuration}
                required
                placeholder="60"
                min="15"
                step="15"
              />

              <Input
                label="Garantie (mois)"
                type="number"
                value={formData.warrantyMonths}
                onChange={(e) => handleChange('warrantyMonths', e.target.value)}
                placeholder="6"
                min="0"
                description="Période de garantie par défaut: 6 mois"
              />

              <Select
                label="Compétences requises"
                description="Compétences nécessaires pour ce service"
                options={skillOptions}
                value={formData.skillRequirements}
                onChange={(value) => handleChange('skillRequirements', value)}
                multiple
                searchable
              />

              <Checkbox
                label="Autoriser les modifications terrain"
                description="Le technicien peut modifier le service sur place"
                checked={formData.allowFieldAmendments}
                onChange={(e) => handleChange('allowFieldAmendments', e.target.checked)}
              />

              <Checkbox
                label="Validation client requise"
                description="Nécessite l'approbation du client pour les modifications"
                checked={formData.customerValidationRequired}
                onChange={(e) => handleChange('customerValidationRequired', e.target.checked)}
              />
            </div>
          </>
        )}
      </div>

      <ServiceMaterialModal
        isOpen={isMaterialModalOpen}
        onClose={() => setIsMaterialModalOpen(false)}
        service={editedServiceId ? { id: editedServiceId, name: formData.name || selectedNode?.name } : null}
        onMaterialsChange={(_, next) => setServiceMaterials(next)}
      />
    </form>
  );
};

export default ServiceEditPanel;